import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSellerNotifications } from '../hooks/useSellerNotifications';

/** Parse API datetime as UTC when it has no timezone (backend sends Unspecified without "Z"). */
function parseCreatedAt(createdAt) {
  if (!createdAt) return null;
  const s = typeof createdAt === 'string' ? createdAt.trim() : String(createdAt);
  if (!s) return null;
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  return new Date(s + (s.includes('T') ? 'Z' : ''));
}

function formatTime(createdAt, now = new Date()) {
  const d = parseCreatedAt(createdAt);
  if (!d || isNaN(d.getTime())) return '';
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const timeStr = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (isToday) return `Today, ${timeStr}`;
  if (isYesterday) return `Yesterday, ${timeStr}`;
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ', ' + timeStr;
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Renders message with the quoted item name in blue (e.g. "Toy car" sold — ...) */
function renderMessageWithItemName(message) {
  const match = String(message).match(/^"([^"]+)"(.*)$/);
  if (match) {
    return (
      <>
        <span className="text-blue-600 dark:text-blue-400 font-medium">"{match[1]}"</span>
        {match[2]}
      </>
    );
  }
  return message;
}

export default function NotificationBell({ isVendor }) {
  const { notifications, unreadCount, markAsRead, markAllAsRead, refetch } = useSellerNotifications(isVendor);
  const [open, setOpen] = useState(false);
  const [timeTick, setTimeTick] = useState(0);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    refetch();
  }, [open, refetch]);

  // Re-render every minute while panel is open so "Just now" updates to actual time
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => setTimeTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [open]);

  if (!isVendor) return null;

  const handleToggle = () => setOpen((o) => !o);

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="relative p-1 rounded-full text-white hover:text-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
        aria-label={unreadCount > 0 ? `${unreadCount} notifications` : 'Notifications'}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-orange-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-4 right-4 top-14 z-50 w-auto max-h-[min(65vh,calc(100vh-11rem))] overflow-hidden rounded-xl bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 flex flex-col md:absolute md:left-auto md:right-0 md:top-full md:mt-2 md:w-[min(320px,calc(100vw-2rem))]">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between shrink-0">
            <span className="font-semibold text-slate-900 dark:text-white">Notifications</span>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="text-sm text-orange-600 hover:text-orange-700"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto min-h-0 flex-1 overscroll-contain">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-500 dark:text-slate-400 text-sm">
                No notifications yet
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                {notifications.map((n) => (
                  <li key={n.id} className={n.readAt ? 'bg-slate-50/50 dark:bg-slate-800/50' : 'bg-white dark:bg-slate-800'}>
                    {n.itemId != null ? (
                      <Link
                        to={`/item/${n.itemId}`}
                        onClick={() => {
                          markAsRead(n.id);
                          setOpen(false);
                        }}
                        className="block px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <span
                            className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                              n.type === 'LowStock'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            }`}
                          >
                            {n.type === 'LowStock' ? '⚠' : '✓'}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900 dark:text-white text-sm">{n.title}</p>
                            <p className="text-slate-600 dark:text-slate-300 text-sm mt-0.5">
                              {renderMessageWithItemName(n.message)}
                            </p>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                              {formatTime(n.createdAt, new Date())}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ) : (
                      <div
                        className="block px-4 py-3"
                      >
                        <div className="flex items-start gap-2">
                          <span className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            ✓
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900 dark:text-white text-sm">{n.title}</p>
                            <p className="text-slate-600 dark:text-slate-300 text-sm mt-0.5">{n.message}</p>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                              {formatTime(n.createdAt, new Date())}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
